import { useEffect, useRef } from 'react';

interface WaveformVisualizerProps {
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  isActive: boolean;
  isRecording?: boolean; // True when actively recording, false for preview/paused
  width?: number;
  height?: number;
}

export function WaveformVisualizer({
  audioContext,
  analyser,
  isActive,
  isRecording = false,
  width = 150,
  height = 60,
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastAmplitudeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    const centerY = height / 2;
    const maxAmplitude = height - 10; // Double the amplitude (use nearly full height)
    const noiseFloor = 10; // Minimum threshold to filter out background noise
    const decay = 0.92; // Decay factor for smoothing

    let dataArray: Uint8Array | null = null;
    if (analyser) {
      const bufferLength = analyser.frequencyBinCount;
      dataArray = new Uint8Array(bufferLength);
    }

    const draw = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let currentAmplitude = 0;

      if (isActive && analyser && dataArray && audioContext) {
        // Get time-domain data for RMS calculation
        // @ts-expect-error - ArrayBufferLike is compatible with Uint8Array at runtime
        analyser.getByteTimeDomainData(dataArray);

        // Calculate RMS (Root Mean Square) for amplitude
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const normalized = (dataArray[i] - 128) / 128; // Normalize to -1 to 1
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        currentAmplitude = rms * 100; // Scale to 0-100

        // Apply noise floor
        if (currentAmplitude < noiseFloor) {
          currentAmplitude = 0;
        }

        // Apply decay for smoothness
        currentAmplitude = Math.max(currentAmplitude, lastAmplitudeRef.current * decay);
        lastAmplitudeRef.current = currentAmplitude;
      } else {
        // Idle state - gentle breathing
        const time = Date.now() / 1000;
        currentAmplitude = Math.abs(Math.sin(time * 1.5)) * 3; // Gentle 0-3% breathing
        lastAmplitudeRef.current = currentAmplitude;
      }

      // Draw the waveform
      drawArtisticWave(ctx, canvas.width, centerY, maxAmplitude, currentAmplitude, isRecording);

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, analyser, audioContext, width, height, isRecording]);

  const drawArtisticWave = (
    ctx: CanvasRenderingContext2D,
    w: number,
    centerY: number,
    maxAmplitude: number,
    amplitude: number,
    isRecording: boolean
  ) => {
    const normalizedAmplitude = (amplitude / 100) * maxAmplitude;
    const time = Date.now() / 1000;

    // Create gradient based on recording state
    const gradient = ctx.createLinearGradient(0, centerY - maxAmplitude / 2, 0, centerY + maxAmplitude / 2);

    if (isRecording) {
      // Active Recording: Color gradient (blue → violet → pink)
      gradient.addColorStop(0, 'rgba(59, 130, 246, 0.6)'); // blue-500
      gradient.addColorStop(0.5, 'rgba(139, 92, 246, 0.8)'); // violet-500
      gradient.addColorStop(1, 'rgba(236, 72, 153, 0.6)'); // pink-500
    } else {
      // Preview/Paused: Grayscale gradient (light gray → medium gray → light gray)
      gradient.addColorStop(0, 'rgba(156, 163, 175, 0.5)'); // gray-400
      gradient.addColorStop(0.5, 'rgba(107, 114, 128, 0.7)'); // gray-500
      gradient.addColorStop(1, 'rgba(156, 163, 175, 0.5)'); // gray-400
    }

    const numBars = 60; // Number of bars across the width
    const barWidth = w / numBars;
    const barSpacing = barWidth * 0.3; // 30% spacing between bars
    const actualBarWidth = barWidth - barSpacing;

    ctx.fillStyle = gradient;
    ctx.shadowBlur = 8;
    ctx.shadowColor = isRecording ? 'rgba(139, 92, 246, 0.3)' : 'rgba(107, 114, 128, 0.2)';

    // Draw vertical bars
    for (let i = 0; i < numBars; i++) {
      const x = i * barWidth + barSpacing / 2;

      // Create organic variation using multiple sine waves with faster movement
      const variation1 = Math.sin((i / numBars) * Math.PI * 6 + time * 4) * 0.4;
      const variation2 = Math.sin((i / numBars) * Math.PI * 11 + time * 3.2) * 0.3;
      const variation3 = Math.sin((i / numBars) * Math.PI * 17 + time * 2.1) * 0.2;
      const variation4 = Math.sin((i / numBars) * Math.PI * 23 + time * 1.5) * 0.15;

      const totalVariation = variation1 + variation2 + variation3 + variation4;

      // Apply envelope shape with 80/20 rule: only center 80% varies, outer 20% stays flat
      const edgeThreshold = 0.1; // 10% on each side
      const normalizedPosition = i / numBars;
      let envelope = 0;
      if (normalizedPosition > edgeThreshold && normalizedPosition < (1 - edgeThreshold)) {
        // Map 0.1-0.9 range to 0-1 range for sine calculation
        const scaledPosition = (normalizedPosition - edgeThreshold) / 0.8;
        envelope = Math.sin(scaledPosition * Math.PI);
      }

      // Calculate bar height based on amplitude and envelope
      const barHeight = normalizedAmplitude * envelope * (1 + totalVariation);

      // Draw bar centered vertically
      if (barHeight > 0) {
        ctx.fillRect(x, centerY - barHeight, actualBarWidth, barHeight * 2);
      }
    }

    // Draw center line
    ctx.shadowBlur = 0;
    // Centerline: violet when recording, gray when preview/paused
    ctx.strokeStyle = isRecording ? 'rgba(139, 92, 246, 0.3)' : 'rgba(156, 163, 175, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(w, centerY);
    ctx.stroke();
  };

  return (
    <div className="flex items-center justify-center">
      <canvas
        ref={canvasRef}
        className="max-w-full"
        style={{ imageRendering: 'auto' }}
        aria-live="polite"
        aria-label={isActive ? 'Microphone active' : 'Microphone idle'}
      />
    </div>
  );
}
